import {
  checkClaimsWeb,
  EventrDate,
  generateID,
  getUserOrgPref,
  IEventInformationData,
  IItinerarymap,
  IRCChannel,
  spError,
  spLog,
  structuredCloneL,
  switchUnixToMs,
  V2,
} from "@eventr/common";
import { createSelector } from "reselect";
import { StateCreator } from "zustand";
import { shallow } from "zustand/shallow";
import { DataAPI, IGeneralInfo } from "../../data/Data";
import { useGuestStore } from "../../feat/guest/guest.store";
import i18n from "../../i18n/i18next";
import accomodationRenderOptions from "../../v2/renderer_options_example/accommodation.json";
import activitiesRenderOptions from "../../v2/renderer_options_example/activities.json";
import eventInfoRenderOptions from "../../v2/renderer_options_example/eventInformationV2.json";
import flightsRenderOptions from "../../v2/renderer_options_example/flights.json";
import hireCarsRenderOptions from "../../v2/renderer_options_example/hireCars.json";
import parkingRenderOptions from "../../v2/renderer_options_example/parkingBookings.json";
import transportRenderOptions from "../../v2/renderer_options_example/transport.json";
import vehicleAllocationsRenderOptions from "../../v2/renderer_options_example/vehicle_allocation.json";
import { BoundStore, bs, useBoundStore } from "../bound.store";
import { IItinerary } from "../interfaces";
import { partitionHiddenItineraries } from "./hidden-itineraries";
import { defaultAddSnack } from "../overlayState/overlayContext";
import { removeOldItineraryData } from "./helpers";
import { migrateEventData } from "./itineraryStateUtils";
import { isCreatingItinerary } from "./new-itinerary-flow";
interface ItineraryMapState {
  /** Visible to this user. Hidden entries are filtered out on the way in, see setItineraryMap. */
  itineraryMap: IItinerarymap[];
  /** Only read by the Itineraries page's Hidden section. */
  hiddenItineraryMap: IItinerarymap[];
  noItineraries: boolean;
  /**
   * True once this session's first itinerary map fetch has answered, or failed, or could not run
   * because the WordPress user never loaded. Until then
   * `noItineraries` is only the starting value. Not persisted.
   */
  itineraryMapSettled: boolean;
  itineraryData: { [key: string]: IItinerary };
}

interface ItineraryMapActions {
  setItineraryMap: (itineraryMap: IItinerarymap[]) => void;
  setToNextItinerary: () => void;
  getSelectedItinCode: () => string;
  findCurrentItineraryIndex: () => number;
  forceItineraryMapUpdate: () => Promise<any[]>;
  createItinerary: (itinerary: IEventInformationData) => Promise<any>;
  resolveItinId: () => Promise<void>;
  canDeleteItinerary: (itineraryID: number) => boolean;
  canHideItinerary: () => boolean;
  hideItinerary: (itineraryID: number) => Promise<boolean>;
  unhideItinerary: (itineraryID: number) => Promise<boolean>;
  deleteItinerary: (itineraryID: number) => Promise<void>;
  getItineraryByID: (id: number) => IItinerary | undefined;
  pullEventData: (itineraryId: number) => Promise<IItinerary | null>;
}

export interface ItineraryMapSlice extends ItineraryMapState, ItineraryMapActions {}

// Keyed by org, itinerary and claimsVersion: a repeat call joins the running request only
// when it would fetch the same thing.
const pullEventDataInFlight = new Map<string, Promise<IItinerary | null>>();

export const createItineraryMapSlice: StateCreator<BoundStore, [["zustand/devtools", never]], [], ItineraryMapSlice> = (
  set,
  get,
) => ({
  itineraryMap: [],
  hiddenItineraryMap: [],
  noItineraries: true,
  itineraryMapSettled: false,
  itineraryData: {},
  setItineraryMap: (itineraryMap) => {
    const { visible, hidden } = partitionHiddenItineraries(itineraryMap);

    set(
      {
        itineraryMap: visible,
        hiddenItineraryMap: hidden,
        noItineraries: visible.length === 0,
        itineraryMapSettled: true,
      },
      false,
      "setItineraryMap",
    );
  },
  setToNextItinerary: () => {
    console.log("setToNextItinerary");

    const { itineraryMap, setCurrentItin, findCurrentItineraryIndex } = get();
    if (!itineraryMap || itineraryMap.length < 1) {
      setCurrentItin(-1);
      return;
    }

    const currentIndex = findCurrentItineraryIndex();

    if (currentIndex !== -1) {
      const nextIndex = currentIndex + 1;
      setCurrentItin(nextIndex < itineraryMap.length ? itineraryMap[nextIndex]!.id : itineraryMap[0]!.id);
    } else {
      setCurrentItin(itineraryMap[0]!.id);
    }
  },
  getSelectedItinCode: () => {
    const { itineraryMap, selectedItinID } = get();
    if (selectedItinID === -1) {
      return "";
    }

    return (
      itineraryMap
        .map((itin) => (itin.id === selectedItinID ? itin.properties?.code : undefined))
        .find((code) => code !== undefined) || ""
    );
  },
  findCurrentItineraryIndex: () => {
    const { itineraryMap, selectedItinID } = get();
    return itineraryMap.findIndex((itinerary: IItinerarymap) => itinerary.id === selectedItinID);
  },
  forceItineraryMapUpdate: async () => {
    const { wpUser, setItineraryMap } = get();
    if (!wpUser && !useGuestStore.getState().isGuestMode) {
      return Promise.resolve([]);
    }

    let newItineraryMap: IItinerarymap[] | undefined;
    try {
      newItineraryMap = await DataAPI.getItineraryMap();
    } finally {
      if (!newItineraryMap) set({ itineraryMapSettled: true }, false, "itineraryMapSettled");
    }

    if (newItineraryMap) {
      const sortedNewItineraryMap = sortItineraryMap(newItineraryMap);

      setItineraryMap(sortedNewItineraryMap ?? []);

      // The visible list, not what the server sent: callers use this to decide whether any
      // itinerary is left to move to.
      return get().itineraryMap;
    }
    return Promise.resolve([]);
  },
  createItinerary: async (itinerary: IEventInformationData) => {
    // The caller owns the spinner: it also owns the guard that keeps Done from firing this
    // twice, and the global flag re-renders every mounted native view's backdrop at once.
    const { wpUser, forceItineraryMapUpdate, setCurrentItin } = get();

    const respCreate = await DataAPI.createItinerary({
      name: itinerary.title ?? "",
    });

    if (!respCreate || !respCreate.data || !respCreate.data.itineraryID) {
      /// Error
      return false;
    }

    const itineraryBaseWithMembers = itinerary;

    itineraryBaseWithMembers.members = [
      {
        option: wpUser?.id,
        type: "User",
        _id: generateID(respCreate.data.itineraryID, 1),
      },
    ];

    const generalInfo: IGeneralInfo = {
      itinerary_id: respCreate.data.itineraryID,
      section_id: 1,
      value: itineraryBaseWithMembers,
    };

    const respUpdate = await DataAPI.createUpdateGeneralInfo(generalInfo);

    if (respCreate && respUpdate) {
      await forceItineraryMapUpdate();
      await setCurrentItin(respCreate.data.itineraryID);

      return true;
    } else {
      return false;
    }
  },
  resolveItinId: async () => {
    const { selectedOrg, itineraryMap, setCurrentItin } = get();
    // The create-itinerary form owns the selection until it closes.
    if (isCreatingItinerary()) return;
    if (selectedOrg) {
      if (itineraryMap && itineraryMap.length > 0) {
        // Stored as a string by every writer, while map ids are numbers.
        const defaultItinerary = Number(getUserOrgPref("itinerary", "defaultItinerary", selectedOrg.name));
        let currentEventID = -1;
        const itineraryIds = itineraryMap.map((i) => i.id);

        if (itineraryIds.includes(defaultItinerary)) {
          currentEventID = defaultItinerary;
        } else {
          const currentItinerary = getCurrentItinerary(itineraryMap);
          if (currentItinerary) {
            currentEventID = currentItinerary.id;
          }
        }
        await setCurrentItin(currentEventID);
      }
    }
  },
  canDeleteItinerary: (itineraryID: number) => {
    const { orgConfig, checkAdmin } = get();
    return checkClaimsWeb([`/${itineraryID}`], ["deleteItinerary"]) && (!orgConfig.adminEditOnly || checkAdmin());
  },
  /**
   * Org scope, unlike canDeleteItinerary. Hiding only affects this user's own lists, so
   * anyone who can see an itinerary may hide it.
   */
  canHideItinerary: () => {
    return checkClaimsWeb(["/"], ["hideItinerary"]);
  },
  hideItinerary: async (itineraryID: number) => {
    const { forceItineraryMapUpdate, setCurrentItin, setToNextItinerary, canHideItinerary, selectedItinID } = get();

    if (!canHideItinerary()) {
      defaultAddSnack(i18n.t("snack.not-allowed"), "error");
      return false;
    }

    try {
      await DataAPI.hideItinerary(itineraryID);
      const visible = await forceItineraryMapUpdate();

      // Hiding the itinerary being viewed has to land on another one, not on a blank Home.
      if (selectedItinID === itineraryID) {
        setCurrentItin(-1);
        if (visible.length !== 0) {
          setToNextItinerary();
        }
      }

      return true;
    } catch (error) {
      spLog("An error while hiding itinerary", error, ["HIDE ITIN"]);
      defaultAddSnack(i18n.t("snack.hide-itinerary-failed"), "error");
      return false;
    }
  },
  unhideItinerary: async (itineraryID: number) => {
    const { forceItineraryMapUpdate } = get();

    try {
      await DataAPI.unhideItinerary(itineraryID);
      await forceItineraryMapUpdate();
      return true;
    } catch (error) {
      spLog("An error while unhiding itinerary", error, ["HIDE ITIN"]);
      defaultAddSnack(i18n.t("snack.unhide-itinerary-failed"), "error");
      return false;
    }
  },
  deleteItinerary: async (itineraryID: number) => {
    const { forceItineraryMapUpdate, setCurrentItin, setToNextItinerary, canDeleteItinerary } = get();
    const archiveTag = 1;

    try {
      if (!canDeleteItinerary(itineraryID)) {
        defaultAddSnack(i18n.t("snack.not-allowed"), "error");
        return;
      }
      await DataAPI.addTag(archiveTag, itineraryID);
      const newMap = await forceItineraryMapUpdate();
      setCurrentItin(-1);

      if (newMap.length !== 0) {
        setToNextItinerary();
      }
    } catch (error) {
      spLog("An error while archiving itinerary", error, ["DELETE ITIN"]);
    }
  },
  getItineraryByID: (id: number) => {
    const { itineraryData } = get();
    const data = itineraryData[id];
    if (data) {
      return replaceRenderOptions(data);
    }
    return itineraryData[id];
  },
  pullEventData: (itineraryId: number) => {
    const key = `${bs().selectedOrg?.name}:${itineraryId}:${bs().claimsVersion}`;
    const inFlight = pullEventDataInFlight.get(key);
    if (inFlight) return inFlight;
    const request = (async () => {
      const map = bs().itineraryMap;

      const itineraryMapItinerary: IItinerarymap | undefined = map.find((e) => e?.id === itineraryId);
      if (itineraryMapItinerary) {
        const eventData: IItinerary | null = await DataAPI.getData(itineraryMapItinerary).catch((e) => {
          spError(e);
          return null;
        });
        if (eventData) {
          // Setting itineraryData below triggers the calendar reconcile subscription in bound.store.
          set(
            {
              itineraryData: {
                ...bs().itineraryData,
                [itineraryId]: eventData,
              },
            },
            false,
            "itineraryData updated",
          );
          migrateEventData(eventData, itineraryId);
        }
        return eventData;
      } else {
        return null;
      }
    })().finally(() => pullEventDataInFlight.delete(key));
    pullEventDataInFlight.set(key, request);
    return request;
  },
});

/**
 * Starts this module's store subscriptions. `startStoreSubscriptions` (state/store-subscriptions.ts)
 * calls it once, from the app entry, after the whole store graph has loaded.
 */
export function startItineraryMapSubscriptions() {
  useBoundStore.subscribe(
    (state) => state.itineraryMap,
    (map) => {
      if (bs().loginCompleted) {
        const selectedItinID = bs().selectedItinID;

        if (selectedItinID !== -1) {
          const localItineraryData = bs().getItineraryByID(selectedItinID);

          const mapVersion = Number.parseInt(map.find((i) => i?.id === selectedItinID)?.itinerary_version ?? "0");
          if (!mapVersion) {
            bs().setCurrentItin(-1);
          }

          const versionMatch = localItineraryData?.version === mapVersion;
          if (!versionMatch) {
            bs().pullEventData(getItineraryMapByID(selectedItinID)?.id ?? -1);
          }

          removeOldItineraryData(map); // TODO : replace
        }

        /// if id is -1 needs to be set if can, unless a new itinerary is being created:
        /// adopting one there drops its draft and loads the adopted itinerary into the open
        /// form.
        if (selectedItinID === -1 && map && map.length > 0 && !isCreatingItinerary()) {
          const selectedOrg = bs().selectedOrg;
          if (selectedOrg) {
            const defaultItinerary = Number(getUserOrgPref("itinerary", "defaultItinerary", selectedOrg.name));

            const exist = map.some((i: IItinerarymap) => i.id === defaultItinerary);

            if (exist) {
              bs().setCurrentItin(defaultItinerary);
            } else {
              bs().setCurrentItin(getCurrentItinerary(map)?.id ?? -1);
            }
          }
        }
      }
    },
    { fireImmediately: true },
  );
  // Keyed on the selected itinerary's own data, so a fetch of another itinerary leaves it be.
  useBoundStore.subscribe(
    (state) => ({
      itin: state.itineraryData[state.selectedItinID],
      selectedItinID: state.selectedItinID,
    }),
    ({ itin, selectedItinID }) => {
      if (selectedItinID !== -1) {
        if (itin) {
          useBoundStore.setState(
            {
              selectedItinerary: replaceRenderOptions(itin),
            },
            false,
            "selectedItinerary updated from itineraryData",
          );
        }
      }
    },
    { fireImmediately: true, equalityFn: shallow },
  );
  // When claim changes, force refetch of itinerary (due to potential hidden data)
  useBoundStore.subscribe(
    (state) => state.claimsVersion,
    (claimsVersion, prevClaimsVersion) => {
      if (prevClaimsVersion !== undefined && claimsVersion !== prevClaimsVersion) {
        const selectedItinID = bs().selectedItinID;
        if (selectedItinID !== -1 && bs().loginCompleted) {
          bs().pullEventData(selectedItinID);
        }
      }
    },
    { fireImmediately: false },
  );
}

export const getItineraryMapByID = (id: number) => {
  const map = bs().itineraryMap;

  const itinMap = map.find((itineraryObj: IItinerarymap) => {
    return itineraryObj?.id === id;
  });

  return itinMap;
};
export const getPubItineraryName = (code: string) => {
  const map = bs().itineraryMap;
  const currentItin = bs().selectedItinID;

  const itinMap = map.find((itineraryObj: IItinerarymap) => {
    return itineraryObj.id === currentItin;
  });
  if (itinMap) {
    return itinMap?.properties?.originalItinCodes?.find((o) => o.code === code)?.name ?? "Imported Activities";
  }

  return itinMap;
};

function sortItineraryMap(unsortedItineraryMap: IItinerarymap[]): IItinerarymap[] {
  if (!unsortedItineraryMap) {
    return [];
  }
  if (unsortedItineraryMap.length < 2) {
    return [...unsortedItineraryMap];
  }

  /// Unix Now
  const timeNow = Math.floor(stripTime(new EventrDate()).getUnixMs / 1000);

  /// NEED TO ADD CURRENT
  /// Sorting based on start time. Time is probally only a date but should show future itins in order THEN past itins in order of start time... i guess.
  const oldItineraries = unsortedItineraryMap
    .filter((itinerary: IItinerarymap) => {
      return switchUnixToMs(itinerary.start_time) < timeNow;
    })
    .sort((itineraryA: IItinerarymap, itineraryB: IItinerarymap) => {
      if (itineraryA.start_time > itineraryB.start_time) {
        return 1;
      } else {
        return -1;
      }
    });

  const futureItineries = unsortedItineraryMap
    .filter((itinerary: IItinerarymap) => {
      return switchUnixToMs(itinerary.start_time) >= timeNow;
    })
    .sort((itineraryA: IItinerarymap, itineraryB: IItinerarymap) => {
      if (itineraryA.start_time > itineraryB.start_time) {
        return 1;
      } else if (itineraryA.start_time === itineraryB.start_time) {
        if (itineraryA.end_time > itineraryB.end_time) {
          return 1;
        } else {
          return -1;
        }
      } else {
        return -1;
      }
    });

  const toReturn = [...futureItineries, ...oldItineraries];
  return toReturn;
}
const stripTime = (date: EventrDate) => {
  date.setFromObject({ hour: 12, minute: 0, second: 0, millisecond: 0 });
  return date;
};

const getCurrentItinerary = (dataItin: IItinerarymap[]) => {
  try {
    let current = dataItin.find(
      (el) => Date.now() < el.end_time * 1000 && Date.now() > el.start_time * 1000, // *1000 to match precision
    );
    //find next upcoming itinerary
    if (current == null) {
      let smallestDiff: number;
      let res;
      dataItin.forEach((el) => {
        const diff = el.start_time * 1000 - Date.now();
        if (diff > 0) {
          if (!smallestDiff || smallestDiff > diff) {
            smallestDiff = diff;
            res = el;
          }
        }
      });
      if (res != null) {
        current = res;
      }
    }
    //find the most recent itinerary
    if (current == null) {
      let smallestDiff = Date.now();
      let res;
      dataItin.forEach((el) => {
        const diff = Date.now() - el.end_time * 1000;
        if (diff > 0) {
          if (smallestDiff > diff) {
            smallestDiff = diff;
            res = el;
          }
        }
      });
      if (res != null) {
        current = res;
      }
    }
    return current;
  } catch {
    return undefined;
  }
};

const getRenderOptions = (sectionID: number) => {
  switch (sectionID) {
    case 1:
      return eventInfoRenderOptions;
    case 2:
      return flightsRenderOptions;
    case 3:
      return accomodationRenderOptions;
    case 4:
      return hireCarsRenderOptions;
    case 5:
      return transportRenderOptions;
    case 8:
      return activitiesRenderOptions;
    case 9:
      return parkingRenderOptions;
    case 10:
      return vehicleAllocationsRenderOptions;
    default:
      return null;
  }
};
export const replaceRenderOptions = (itin: IItinerary) => {
  const newV2:
    | V2[]
    | {
        rendererOptions:
          | {
              components: {
                componentKey: string;
                points: (
                  | {
                      extraMidValues: {
                        componentKey: string;
                        darkMode: boolean;
                        typography: string;
                        value: string;
                      }[];
                      midValue: {
                        componentKey: string;
                        darkMode: boolean;
                        typography: string;
                        value: string;
                      };
                      onClick: {
                        action: string;
                        params: {
                          key: string;
                          value: string;
                        }[];
                      };
                    }
                  | {
                      midValue: {
                        componentKey: string;
                        darkMode: boolean;
                        typography: string;
                        value: string;
                      };
                      onClick: {
                        action: string;
                        params: { value: string }[];
                      };
                      extraMidValues?: undefined;
                    }
                )[];
              }[];
              settings: { displayType: string; headerIcon: string };
            }
          | {
              components: { componentKey: string }[];
              settings: {
                dashboardKey: string;
                displayInUpNext: boolean;
                editForm: string;
                editHeader: string;
                filterCriteria: string[];
                headerIcon: string;
                isHeaderExpandable: boolean;
                pastCriteria: string;
                pastHeader: string;
                pastHeaderIcon: string;
                primaryLabel: string;
                secondaryCard: string;
                secondaryLabel: string;
                userList: string;
                wizardHeader: string;
              };
            }
          | {
              components: { componentKey: string }[];
              settings: {
                dashboardKey: string;
                displayInUpNext: boolean;
                editForm: string;
                editHeader: string;
                headerIcon: string;
                isHeaderExpandable: boolean;
                multiDays: boolean;
                pastCriteria: string;
                pastHeader: string;
                pastHeaderIcon: string;
                primaryLabel: string;
                secondaryCard: string;
                secondaryLabel: string;
                userList: string;
                wizardHeader: string;
              };
            }
          | {
              components: (
                | {
                    componentKey: string;
                    height: string;
                    body?: undefined;
                    border?: undefined;
                  }
                | {
                    body: (
                      | {
                          componentKey: string;
                          typography: string;
                          value: string;
                          components?: undefined;
                        }
                      | {
                          componentKey: string;
                          value: string;
                          components: (
                            | {
                                componentKey: string;
                                hidden: string[];
                                visible: string[];
                                points: (
                                  | {
                                      componentKey: string;
                                      height: string;
                                      endValue?: undefined;
                                      midValue?: undefined;
                                      noSubItemsPadding?: undefined;
                                      subItems?: undefined;
                                    }
                                  | {
                                      endValue: {
                                        addUserConfirmationHeader: string;
                                        addUserConfirmationText: string;
                                        channelNames: string[];
                                        componentKey: string;
                                        hidden: string[];
                                        usersList: string;
                                        visible: string[];
                                      };
                                      midValue: {
                                        componentKey: string;
                                        typography: string;
                                        value: string;
                                      };
                                      noSubItemsPadding: boolean;
                                      subItems: (
                                        | {
                                            componentKey: string;
                                            height: string;
                                            value?: undefined;
                                            typography?: undefined;
                                            users?: undefined;
                                          }
                                        | {
                                            componentKey: string;
                                            value: string;
                                            typography: string;
                                            height?: undefined;
                                            users?: undefined;
                                          }
                                        | {
                                            componentKey: string;
                                            users: string;
                                            height?: undefined;
                                            value?: undefined;
                                            typography?: undefined;
                                          }
                                      )[];
                                      componentKey?: undefined;
                                      height?: undefined;
                                    }
                                )[];
                              }
                            | {
                                componentKey: string;
                                hidden: string[];
                                visible: string[];
                                points: (
                                  | {
                                      componentKey: string;
                                      height: string;
                                      subItemsVisible?: undefined;
                                      endValue?: undefined;
                                      midValue?: undefined;
                                      noSubItemsPadding?: undefined;
                                      subItems?: undefined;
                                    }
                                  | {
                                      subItemsVisible: string[];
                                      endValue: {
                                        addUserConfirmationHeader: string;
                                        addUserConfirmationText: string;
                                        channelNames: string[];
                                        componentKey: string;
                                        hidden: string[];
                                        usersList: string;
                                        visible: string[];
                                      };
                                      midValue: {
                                        componentKey: string;
                                        typography: string;
                                        value: string;
                                      };
                                      noSubItemsPadding: boolean;
                                      subItems: (
                                        | {
                                            componentKey: string;
                                            height: string;
                                            value?: undefined;
                                            typography?: undefined;
                                            users?: undefined;
                                          }
                                        | {
                                            componentKey: string;
                                            value: string;
                                            typography: string;
                                            height?: undefined;
                                            users?: undefined;
                                          }
                                        | {
                                            componentKey: string;
                                            users: string;
                                            height?: undefined;
                                            value?: undefined;
                                            typography?: undefined;
                                          }
                                      )[];
                                      componentKey?: undefined;
                                      height?: undefined;
                                    }
                                )[];
                              }
                          )[];
                          typography?: undefined;
                        }
                    )[];
                    border: boolean;
                    componentKey: string;
                    height?: undefined;
                  }
              )[];
              settings: {
                headerIcon: string;
                isHeaderExpandable: boolean;
                userList: string;
              };
            };
        sectionID: number;
        sectionName: string;
        sectionDisplayName: string;
        sectionDisplayNameTeams: string;
        renderer: "universal-iterable" | "universal" | "pastSorter";
        RCChannels: IRCChannel[];
        data: IEventInformationData | any;
        sectionNameOverwrite?: string;
        category?: string;
        iconOverwrite?: string;
        usePage?: boolean;
        ogCode?: string;
      }[]
    | undefined = [];
  itin.v2?.forEach((v2) => {
    const rendererOptions = getRenderOptions(v2.sectionID);
    if (rendererOptions) {
      newV2.push({ ...v2, rendererOptions });
    }
  });
  const copy = structuredCloneL({ ...itin, v2: [] }) as IItinerary;
  copy.v2 = newV2;
  return copy;
};

export const selectorOriginalItinCodes = createSelector(
  (state: BoundStore) => state.itineraryMap,
  (state: BoundStore) => state.selectedItinID,
  (map, itinID) => {
    const currentItin = map.find((i) => i.id === itinID);
    return currentItin?.properties?.originalItinCodes;
  },
);
